import React from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Users, Navigation, Clock } from "lucide-react";
import { getEventImage } from "../../utils/eventImageUtils";
import { getEventIcon } from "../../utils/eventIconUtils";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import bookedStamp from "../../assets/booked.png";
import cancelledStampImage from "../../assets/cancelledStampImage.png";

const BookingEventCard = ({ event, isCancelled, onViewBooking }) => {
  const navigate = useNavigate();
  const isEventCancelled = isCancelled || event?.isCancelled || event?.lifecycleStatus === "CANCELLED";
  const eventTypeName =
    event.eventTypeText ||
    (typeof event.eventType === "object" && event.eventType !== null
      ? event.eventType.name
      : event.eventType) ||
    "Event";
  const EventIcon = getEventIcon(eventTypeName);

  const locationsArr = Array.isArray(event.locations)
    ? event.locations
    : event.location
    ? [event.location]
    : [];
  const locationNames = locationsArr
    .map((l) => (typeof l === "object" && l !== null ? (l.name || l.city || "") : l))
    .filter(Boolean);
  const locationName =
    locationNames.length > 0
      ? locationNames.join(", ")
      : typeof event.location === "string"
      ? event.location
      : "Location TBD";

  const handleCardClick = () => {
    if (onViewBooking) {
      onViewBooking(event);
    } else {
      navigate(`/events/${event._id}/booking-overview`);
    }
  };

  const handleHelpClick = (e) => {
    e.stopPropagation();
    window.location.href = "/help";
  };

  const handleViewBookingClick = (e) => {
    e.stopPropagation();
    if (onViewBooking) {
      onViewBooking(event);
    } else {
      navigate(`/events/${event._id}/booking-overview`);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="relative w-full h-full rounded-2xl bg-white flex flex-col cursor-pointer border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 p-2 sm:p-3"
    >
      {/* Stamp Overlay - Positioned with respect to the card container at the top left */}
      <img
        src={isEventCancelled ? cancelledStampImage : bookedStamp}
        alt={isEventCancelled ? "Cancelled stamp" : "Booked stamp"}
        className="absolute top-[-10px] left-[-10px] sm:top-[-24px] sm:left-[-24px] md:top-[-28px] md:left-[-28px] w-14 h-14 sm:w-24 sm:h-24 md:w-28 md:h-28 rotate-[-20deg] opacity-100 drop-shadow-[0_4px_8px_rgba(0,0,0,0.18)] pointer-events-none select-none z-30"
      />

      {/* Image Section */}
      <div className="relative w-full aspect-[4/3] mb-1.5 sm:mb-3">
        <div className="w-full h-full rounded-xl overflow-hidden relative">
          <img
            src={getEventImage(eventTypeName)}
            alt={event.eventName}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-0.5 sm:px-1.5 flex flex-col">
        <h3 className="text-[12px] xs:text-[13px] sm:text-lg md:text-xl leading-tight font-bold text-black line-clamp-2 min-h-[30px] sm:min-h-0 mb-1 sm:mb-0">
          {capitalizeFirstLetter(event.eventName)}
        </h3>

        {/* Info grid (2 columns) */}
        <div className="grid grid-cols-2 gap-x-1 sm:gap-x-2 gap-y-1 sm:gap-y-3 my-1.5 sm:my-3 sm:mb-5">
          <div className="flex items-center gap-1 sm:gap-2 text-[#FF4C18]">
            <Calendar className="w-3 h-3 sm:w-5 sm:h-5 shrink-0" />
            <span className="text-[9px] xs:text-[10px] sm:text-[14px] text-gray-700 font-medium truncate">
              {new Date(event.startDate).toLocaleDateString("en-GB")}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 text-[#FF4C18]">
            <EventIcon className="w-3 h-3 sm:w-5 sm:h-5 shrink-0" />
            <span className="text-[9px] xs:text-[10px] sm:text-[14px] text-gray-700 font-medium capitalize truncate">
              {eventTypeName}
            </span>
          </div>
          {event.roleType === "planner" ||
          event.requirementType === "planner" ||
          event.roleType === "Customer" ? (
            <div className="flex items-center gap-1 sm:gap-2 text-[#FF4C18]">
              <Users className="w-3 h-3 sm:w-5 sm:h-5 shrink-0" />
              <span className="text-[9px] xs:text-[10px] sm:text-[14px] text-gray-700 font-medium truncate">
                {event.guests || 0} pax
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1 sm:gap-2 text-[#FF4C18]">
              <Clock className="w-3 h-3 sm:w-5 sm:h-5 shrink-0" />
              <span className="text-[9px] xs:text-[10px] sm:text-[14px] text-gray-700 font-medium truncate">
                {event.durationHours || 1} hrs
              </span>
            </div>
          )}
          <div className="flex items-center gap-1 sm:gap-2 text-[#FF4C18]">
            <Navigation className="w-3 h-3 sm:w-5 sm:h-5 rotate-45 shrink-0" />
            <span
              title={locationName}
              className="text-[9px] xs:text-[10px] sm:text-[14px] text-gray-700 font-medium truncate"
            >
              {locationName}
            </span>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="mt-auto flex gap-1 sm:gap-2 md:gap-3 pt-1 mb-1">
          <button
            type="button"
            onClick={handleHelpClick}
            className="flex-[0.8] sm:flex-1 min-w-0 h-7 sm:h-11 flex items-center justify-center rounded-[6px] sm:rounded-[10px] border border-[#FF4C18] text-[#FF4C18] bg-white hover:bg-orange-50 px-1 sm:px-2 text-[9px] xs:text-[10px] sm:text-[12px] lg:text-[11px] xl:text-[12px] 2xl:text-[14px] font-semibold transition whitespace-nowrap tracking-tight sm:tracking-normal"
          >
            Help
          </button>

          <button
            type="button"
            onClick={handleViewBookingClick}
            className="flex-[1.2] sm:flex-1 min-w-0 h-7 sm:h-11 flex items-center justify-center rounded-[6px] sm:rounded-[10px] bg-[#FF4C18] hover:bg-[#e64415] px-1 sm:px-2 text-white text-[9px] xs:text-[10px] sm:text-[12px] lg:text-[11px] xl:text-[12px] 2xl:text-[14px] font-semibold transition whitespace-nowrap tracking-tight sm:tracking-normal"
          >
            View Booking
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingEventCard;
